import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Camera, CheckCircle, XCircle, Clock, Eye, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const AdminGallery = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [pending, setPending] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [moderating, setModerating] = useState(null);

  // Check admin access
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    } else if (currentUser.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [currentUser, navigate]);

  // Fetch data
  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchData();
    }
  }, [currentUser]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [pendingRes, statsRes] = await Promise.all([
        api.get('/api/gallery/pending'),
        api.get('/api/gallery/stats')
      ]);
      setPending(pendingRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load gallery data');
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = async (imageId, action) => {
    setModerating(imageId);
    
    try {
      await api.post(`/api/gallery/moderate/${imageId}`, null, {
        params: { action }
      });
      
      toast.success(`Photo ${action}d successfully!`);
      
      // Remove from pending list
      setPending(pending.filter(p => p.id !== imageId));
      
      // Update stats
      fetchData();
    } catch (error) {
      console.error('Error moderating:', error);
      toast.error('Failed to moderate photo');
    } finally {
      setModerating(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-lime-400 border-t-transparent mx-auto mb-4"></div>
          <p className="text-zinc-400">Loading gallery...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white py-20">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">
            <ImageIcon className="inline-block w-10 h-10 mr-3 text-lime-400" />
            Gallery Management
          </h1>
          <p className="text-zinc-400">Review and moderate customer photo submissions</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Total Photos</p>
                  <p className="text-3xl font-bold text-white">{stats.total || 0}</p>
                </div>
                <Camera className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Approved</p>
                  <p className="text-3xl font-bold text-lime-400">{stats.approved || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-lime-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-lime-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Pending Review</p>
                  <p className="text-3xl font-bold text-yellow-400">{stats.pending || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Rejected</p>
                  <p className="text-3xl font-bold text-red-400">{stats.rejected || 0}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Customer Uploads</p>
                  <p className="text-3xl font-bold text-white">{stats.customer_uploads || 0}</p>
                </div>
                <ImageIcon className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Submissions */}
        <Card className="bg-zinc-900 border-zinc-800 mb-8">
          <CardHeader>
            <CardTitle className="text-lime-400">
              Pending Review ({pending.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pending.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pending.map(photo => (
                  <Card key={photo.id} className="bg-zinc-800 border-yellow-500">
                    <div className="relative">
                      <img
                        src={photo.url}
                        alt={photo.alt}
                        className="w-full h-64 object-cover rounded-t-lg"
                      />
                      <Badge className="absolute top-3 right-3 bg-yellow-500 text-black">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending
                      </Badge>
                    </div>
                    <CardContent className="p-4 space-y-3">
                      <div>
                        <p className="text-white font-semibold">{photo.customer_name || 'Anonymous'}</p>
                        <p className="text-zinc-400 text-sm">
                          Submitted {new Date(photo.created_at).toLocaleDateString()}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          onClick={() => handleModerate(photo.id, 'approve')}
                          disabled={moderating === photo.id}
                          className="bg-lime-500 hover:bg-lime-600 text-black"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          {moderating === photo.id ? 'Processing...' : 'Approve'}
                        </Button>
                        <Button
                          onClick={() => handleModerate(photo.id, 'reject')}
                          disabled={moderating === photo.id}
                          variant="outline"
                          className="border-red-400 text-red-400 hover:bg-red-400 hover:text-black"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
                <h3 className="text-xl font-semibold mb-2">All Caught Up!</h3>
                <p className="text-zinc-400">No pending submissions to review</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="flex gap-4">
          <Button
            onClick={() => navigate('/gallery')}
            variant="outline"
            className="border-zinc-700 text-white hover:bg-zinc-800"
          >
            <Eye className="w-4 h-4 mr-2" />
            View Public Gallery
          </Button>
          <Button
            onClick={fetchData}
            variant="outline"
            className="border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black"
          >
            Refresh Data
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminGallery;